// Reject-source display config, shared by Rejects.tsx (capture + review) and
// Runs.tsx (run summary) so the two surfaces can't drift apart again (M71).
//
// Citrus rejects carry one of three fixed enum stages; berry (force-cool)
// rejects carry FREE-TEXT stages (mig 1046 dropped the citrus-only
// `reject_source IN (...)` CHECK). Any consumer that iterates only the fixed
// citrus list therefore renders a blank breakdown + source-less chips for
// every berry reject — the exact defect this module exists to prevent. Gate
// on `useFlowManifest().usesForceCooling` and resolve BOTH shapes.

export type RejectSource = 'PRESORTER' | 'MAF_RODA' | 'PACKER'

export const REJECT_SOURCES: { value: RejectSource; label: string; colour: string }[] = [
  { value: 'PRESORTER', label: 'Presorter', colour: 'bg-purple-700' },
  { value: 'MAF_RODA',  label: 'MAF Roda',  colour: 'bg-orange-700' },
  { value: 'PACKER',    label: 'Packer',    colour: 'bg-blue-700'   },
]

// Seed stages for the berry free-text reject flow (operators can add more).
export const BERRY_REJECT_SOURCES = ['Field', 'Packline']

// Resolve a stored reject_source to a display chip. Citrus values map to their
// fixed config (label + colour); berry / operator-added values render verbatim
// on a neutral chip (they have no fixed colour).
export function sourceChip(src: string | null): { label: string; colour: string } | null {
  if (!src) return null
  const cfg = REJECT_SOURCES.find(s => s.value === src)
  return cfg ? { label: cfg.label, colour: cfg.colour } : { label: src, colour: 'bg-slate-600' }
}

// The ordered source rows to break a run's rejects down by. Citrus → the three
// fixed stages; berry → the distinct free-text stages actually present on the
// run, on a neutral colour (they have no fixed palette). Keeps both the Runs
// summary and the Rejects review off the hard-coded citrus list so a berry run
// never renders a blank breakdown.
export function rejectBreakdownSources(
  sources: (string | null)[],
  isBerry: boolean,
): { value: string; label: string; colour: string }[] {
  if (!isBerry) return REJECT_SOURCES
  return Array.from(new Set(sources.filter(Boolean) as string[]))
    .map(v => ({ value: v, label: v, colour: 'bg-slate-500' }))
}
